import React from 'react';
import AddExamResultForm from '@components/exam_result/form/AddExamResultForm';
import { CDN_URL } from '@utils/constants/url.constants';
import { Button, PopUp, Snackbar } from 'geniebook-ui/dist';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { useDeleteExamResult } from '@hooks/student.hook';
import { useViewWidth } from '@utils/helpers/viewWidth';
import { useNavigate } from 'react-router-dom';

const breakpoint = require('geniebook-ui/dist/themes/breakpoint');

interface AddExamResultProps {
  examResultId?: string | null;
}

const AddExamResultPage = ({ examResultId = null }: AddExamResultProps) => {
  const viewWidth = useViewWidth();
  const navigate = useNavigate();
  const [deleteExamResultID, setDeleteExamResultID] = React.useState<
    string | null
  >(null);
  const deleteExamResult = useDeleteExamResult();
  type ErrorResp = {
    error: boolean;
    error_msg: string;
    error_code: string;
  };

  type SuccessResp = {
    success: boolean;
    success_message: string;
  };
  const [error, setError] = React.useState<ErrorResp>({
    error: false,
    error_msg: '',
    error_code: '',
  });

  const [success, setSuccess] = React.useState<SuccessResp>({
    success: false,
    success_message: '',
  });

  const hideModalDelete = () => {
    setDeleteExamResultID(null);
  };

  const dismissSnackbar = () => {
    navigate(`/exam-result`);
  };

  const renderSnackbar = ({
    error,
    success,
  }: {
    error: ErrorResp;
    success: SuccessResp;
  }) => {
    const state = error.error
      ? 'error'
      : success.success
      ? 'celebratory'
      : null;
    const message = error.error ? error.error_msg : success.success_message;
    const buttonText = error.error ? 'Refresh' : '';
    return (
      state && (
        <Snackbar
          className={'fixed center-horizontal top-[35px]'}
          onTextClick={dismissSnackbar}
          states={state}
          message={message}
          buttonText={buttonText}
          views="desktop"
        />
      )
    );
  };

  const onExamResultDelete = async (examResultID: string) => {
    try {
      const formData = new FormData();
      formData.append('student_exam_score_id', examResultID + '');
      const res = await deleteExamResult.mutateAsync(formData);
      const body = res.data;
      if (body.error) {
        setError(prevError => ({
          ...prevError,
          error: body.error,
          error_msg: body.error_msg,
          error_code: body.error_code,
        }));
      } else {
        setSuccess(prevSuccess => ({
          ...prevSuccess,
          success: true,
          success_message: 'Exam Result Deleted Successfully',
        }));

        setTimeout(() => {
          dismissSnackbar();
        }, 1000);
      }
    } catch (error) {
      setError(prevError => ({
        ...prevError,
        error: true,
        error_msg: 'Unknown Error',
        error_code: '',
      }));
    }
  };

  return (
    <>
      <div>
        <div className={'max-lg:hidden flex gap-3 items-center mb-6'}>
          <p className={'text-white text-lg leading-[1.2] font-semibold'}>
            {examResultId ? 'Edit' : 'Add'} Exam Results
          </p>

          {examResultId ? (
            <Button
              className="inline-block !h-[30px] !w-25"
              variant="tertiary-raspberry"
              size="xs"
              onClick={() => {
                setDeleteExamResultID(examResultId);
              }}
            >
              <div className="text-neutral-chalk">
                <FontAwesomeIcon
                  icon={faTrash}
                  className="mr-1 text-neutral-chalk"
                />
                Delete
              </div>
            </Button>
          ) : (
            <div
              className={
                'p-2 rounded-xl bg-primary-skyblue-faded text-primary-navyblue flex items-center font-bold text-sm leading-4 relative'
              }
            >
              <div
                className={
                  'absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 w-2 h-2 rotate-45 bg-primary-skyblue-faded'
                }
              />
              <p>Earn</p>
              <img
                src={`${CDN_URL}assets/icons/general/bubble.svg`}
                alt={'bubble'}
                className="w-4 h-4 ml-1 mr-0.5 inline"
              />
              <p>20</p>
            </div>
          )}
        </div>
        <AddExamResultForm examResultId={examResultId} />
        {examResultId &&
          (viewWidth < breakpoint.lg ? (
            <Button
              className="max-lg:!max-w-full max-lg:!w-full !font-extrabold max-lg:hover:!scale-[1.01]"
              variant="tertiary-raspberry"
              size="xs"
              onClick={() => {
                setDeleteExamResultID(examResultId);
              }}
            >
              <div className="text-neutral-chalk">
                <FontAwesomeIcon
                  icon={faTrash}
                  className="mr-1 text-neutral-chalk"
                />
                Delete
              </div>
            </Button>
          ) : null)}
      </div>
      {deleteExamResultID && (
        <PopUp
          buttonGroup={
            <>
              <Button
                variant="tertiary-raspberry"
                onClick={() => {
                  onExamResultDelete(deleteExamResultID);
                  hideModalDelete();
                }}
              >
                <FontAwesomeIcon icon={faTrash} className="mr-1" />
                Delete
              </Button>
              <Button
                variant="secondary"
                className="!max-w-[100%] w-full"
                onClick={() => {
                  hideModalDelete();
                }}
              >
                Cancel
              </Button>
            </>
          }
          onClose={hideModalDelete}
          width={viewWidth < breakpoint.md ? `${viewWidth - 50}px` : '300px'}
          title={'Delete Entry'}
          description={'This action cannot be undone'}
          disableBodyScroll={true}
          className="!px-8 !py-6"
        />
      )}
      {renderSnackbar({ error, success })}
    </>
  );
};

export default AddExamResultPage;
